<template>
    <div
        v-if="((order.was_successful && !disableChangeButtons) || order.order_insurance) && (!productWasTransferred || modalIsVisible)"
        class="change-order-buttons-container mt-1 mt-md-2 mb-2"
    >
        <div class="row">
            <div class="col">
                <div class="row">
                    <div class="col-12 col-md-6">
                        <button
                            type="button"
                            class="btn btn-block btn-outline-primary"
                            @click="onChangeButtonClick($event, 'license-plate')"
                            v-html="$t('orderChange.changeLicensePlate')"
                        ></button>
                    </div>

                    <div v-if="shouldChangeValidity" class="col-12 col-md-6 p-2">
                        <button
                            type="button"
                            class="btn btn-block btn-outline-primary"
                            @click="onChangeButtonClick($event, 'validity-start')"
                            v-html="$t('orderChange.changeValidityStart')"
                        ></button>
                    </div>

                    <div v-if="isTransferOfProductEnabled && isTransferable" class="col-12 col-md-6 p-2">
                        <button
                            type="button"
                            class="btn btn-block btn-outline-primary"
                            @click="onChangeButtonClick($event, 'transfer')"
                            v-html="$t('orderChange.transferProduct')"
                        ></button>
                    </div>
                </div>
            </div>
        </div>

        <modal
            id="change-order-modal"
            :centered="true"
            :title="modal.title"
            :show="modal.show"
            :close="modal.close"
            :closeable="!change.isOngoing"
            size="modal-lg"
            @modal-close="
                modal.show = false;
                modal.close = false;
            "
            @modal-opening="modalIsVisible = true"
            @modal-invisible="modalIsVisible = false"
            @modal-visible="onModalVisible"
        >
            <div class="modal-body">
                <form
                    v-if="(isChangeable || showTransferForm || change.completed) && !order.system_guest_order"
                    ref="changeForm"
                    action=""
                    class="row"
                    method="post"
                >
                    <div v-if="change.completed" class="col-12">
                        <div
                            v-if="changeLicensePlate"
                            class="alert alert-success"
                            v-html="
                                $t('orderChange.licensePlateChanged', {
                                    country: this.order.country_code,
                                    plate: this.order.license,
                                })
                            "
                        ></div>

                        <div
                            v-if="changeValidity"
                            class="alert alert-success"
                            v-html="
                                $t('orderChange.validityChanged', {
                                    from: this.order.validity_start_string,
                                    until: this.order.validity_end_string,
                                })
                            "
                        ></div>

                        <div
                            v-if="changeProductOwner"
                            class="alert alert-success"
                            v-html="
                                $t('orderChange.transferStarted', {
                                    email: this.transferProduct.email,
                                })
                            "
                        ></div>
                    </div>

                    <div v-if="typeof change.failureReason === 'string' && change.failureReason.length" class="col-12">
                        <div class="alert alert-danger" v-html="change.failureReason"></div>
                    </div>

                    <div v-if="!change.completed && changeLicensePlate" class="col-12">
                        <div class="row">
                            <div class="col-12 mb-3">
                                <label for="evidenceCountry" class="col-form-label">{{
                                    $t('shop.licensing_state')
                                }}</label>
                                <select
                                    id="evidenceCountry"
                                    class="form-control height-auto-override"
                                    name="country_code"
                                    :class="{ 'is-invalid': formErrors.country_code }"
                                    v-model="licensePlateChange.country"
                                    @change="inputChange('country_code')"
                                    required
                                >
                                    <option :value="false" disabled>{{ $t('shop.dropdown_required') }}</option>
                                    <option
                                        v-for="option in licensePlateCountries"
                                        :key="option.country_code"
                                        :value="option.country_code"
                                    >
                                        {{ $t('country.' + option.country_code) }}
                                    </option>
                                </select>
                                <div class="invalid-feedback">
                                    <span class="d-block" v-for="error in formErrors.country_code" :key="error">{{
                                        error
                                    }}</span>
                                </div>
                            </div>

                            <div v-if="selectedLicensePlateCountry !== null" class="col-12 col-lg-6 mb-3 mb-md-0">
                                <label class="col-form-label">{{ $t('shop.license_plate') }}</label>
                                <license-plate-input
                                    :license-plate-country="selectedLicensePlateCountry"
                                    :template="selectedLicensePlateCountry.template"
                                    :required="true"
                                    :is-invalid="
                                        typeof formErrors.license === 'object' && formErrors.license.length > 0
                                    "
                                    name="license"
                                    id="licensePlate"
                                    @input="inputChange('license')"
                                    v-model="licensePlateChange.plate"
                                >
                                    <span class="d-block" v-for="error in formErrors.license" :key="error">{{
                                        error
                                    }}</span>
                                </license-plate-input>
                            </div>
                            <div v-if="selectedLicensePlateCountry !== null" class="col-12 col-lg-6">
                                <label class="col-form-label">{{ $t('shop.license_plate_confirm') }}</label>
                                <license-plate-input
                                    :license-plate-country="selectedLicensePlateCountry"
                                    :template="selectedLicensePlateCountry.template"
                                    :required="true"
                                    :is-invalid="
                                        typeof formErrors.license_confirmation === 'object' &&
                                        formErrors.license_confirmation.length > 0
                                    "
                                    name="license_confirmation"
                                    id="licensePlateConfirm"
                                    @input="inputChange('license_confirmation')"
                                    v-model="licensePlateChange.plateConfirm"
                                >
                                    <span
                                        class="d-block"
                                        v-for="error in formErrors.license_confirmation"
                                        :key="error"
                                        >{{ error }}</span
                                    >
                                </license-plate-input>
                            </div>
                        </div>
                    </div>

                    <div v-if="!change.completed && changeValidity" class="col-12">
                        <label class="col-form-label" @click="this.$refs.datepicker.focus()">
                            {{ $t('shop.validity_start') }}
                        </label>
                        <nova-datepicker
                            ref="datepicker"
                            id="valid_from"
                            name="validity_start"
                            required="required"
                            v-model="validityChange.date"
                            :config="datePickerOptions"
                            @dp-change="inputChange('validity_start')"
                            :class="{ 'is-invalid': formErrors.validity_start }"
                        >
                        </nova-datepicker>
                        <div class="invalid-feedback">
                            <span class="d-block" v-for="error in formErrors.validity_start" :key="error">{{
                                error
                            }}</span>
                        </div>
                    </div>

                    <div v-if="!change.completed && changeProductOwner" class="col-12">
                        <div class="row">
                            <div class="col-12">
                                <p v-html="$t('orderChange.transferModalDescription')"></p>
                            </div>

                            <div class="col-12 col-md-6">
                                <label class="col-form-label" v-html="$t('shop.email')"></label>
                                <input
                                    type="email"
                                    id="orderChangeProductOwnerEmail"
                                    class="form-control"
                                    name="email"
                                    required="required"
                                    v-model="transferProduct.email"
                                    @input="inputChange('email')"
                                    :class="{ 'is-invalid': formErrors.email }"
                                />
                                <div class="invalid-feedback">
                                    <span class="d-block" v-for="error in formErrors.email" :key="error">{{
                                        error
                                    }}</span>
                                </div>
                            </div>
                            <div class="col-12 col-md-6">
                                <label class="col-form-label" v-html="$t('orderChange.emailConfirm')"></label>
                                <input
                                    type="email"
                                    id="orderChangeProductOwnerEmailConfirm"
                                    class="form-control"
                                    name="email_confirm"
                                    required="required"
                                    v-model="transferProduct.emailConfirm"
                                    @input="inputChange('email_confirm')"
                                    :class="{ 'is-invalid': formErrors.email }"
                                />
                                <div class="invalid-feedback">
                                    <span class="d-block" v-for="error in formErrors.email_confirm" :key="error">{{
                                        error
                                    }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
                <div v-else-if="changeProductOwner" class="row">
                    <div class="col-12">
                        <div class="alert alert-danger" v-html="$t('orderChange.notTransferableNotice')"></div>
                    </div>
                </div>
                <div v-else class="row">
                    <div class="col-12">
                        <div class="alert alert-danger" v-html="$t('orderChange.notTransferableNotice')"></div>
                    </div>
                </div>
            </div>

            <div v-if="isChangeable || showTransferForm" class="modal-footer">
                <button
                    v-if="!change.completed"
                    type="button"
                    class="btn btn-secondary"
                    @click="onModalButtonClick($event, false)"
                    :disabled="change.isOngoing"
                    v-html="$t('frontend.abort')"
                ></button>

                <button
                    type="button"
                    class="btn btn-primary"
                    @click="onModalButtonClick($event, !change.completed)"
                    :disabled="change.isOngoing"
                    v-html="modal.confirmButtonLabel"
                ></button>
            </div>
            <div v-else class="modal-footer">
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="onModalButtonClick($event, false)"
                    v-html="$t('frontend.ok')"
                ></button>
            </div>

            <loading-animation
                :visible="change.isOngoing"
                :always-show="false"
                preloader-container-id=""
                status-container-id=""
                :loading-text="loadingAnimationText"
            ></loading-animation>
        </modal>
    </div>
</template>

<script>
export default {
    props: {
        order: {
            type: Object,
            required: true,
        },
        licensePlateCountries: {
            type: Array,
            required: false,
            default() {
                return [
                    {
                        country_name: '',
                        country_code: '',
                        allows_multilicenseplate: false,
                        image: null,
                        placeholder: 'B-AB1234',
                    },
                ];
            },
        },
        defaultLicensePlateCountry: {
            type: String,
            required: false,
            default() {
                return '';
            },
        },
        defaultLicensePlate: {
            type: String,
            required: false,
            default() {
                return '';
            },
        },
        disableChangeButtons: {
            type: Boolean,
            required: true,
        },
        isTransferOfProductEnabled: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        ongoingOrderUpdate: {
            type: Object,
            required: false,
            default() {
                return {
                    id: null,
                    isLicensePlateUpdate: false,
                    licensePlateCountry: '',
                    licensePlate: '',
                    isValidityRangeUpdate: false,
                    validityStart: '',
                    isProductTransfer: false,
                    productTransferEmail: '',
                };
            },
        },
    },

    data() {
        return {
            datePickerOptions: this.$datePickerOptions,
            modal: {
                title: '',
                show: false,
                close: false,
                confirmButtonLabel: '',
            },
            changeProductOwner: false,
            changeLicensePlate: false,
            changeValidity: false,
            change: {
                isOngoing: false,
                id: null,
                interval: -1,
                completed: false,
                failureReason: null,
            },
            licensePlateChange: {
                country: false,
                plate: '',
                plateConfirm: '',
            },
            validityChange: {
                date: '',
            },
            transferProduct: {
                email: '',
                emailConfirm: '',
            },
            formErrors: {},
            changedInputs: {
                country_code: false,
                license: false,
                license_confirmation: false,
                validity_start: false,
            },
            isValid: false,
            modalIsVisible: false,
            ongoingUpdate: this.ongoingOrderUpdate,
            checkStatusInterval: 3000,
        };
    },

    computed: {
        /**
         * Determine if the order is changeable.
         * In the case that the order does not have a ASFiN account linking, we're not able
         * to change the validity or license plate. Additional if the validity period already
         * started we also not able to change anything.
         *
         * @returns {boolean}
         */
        isChangeable() {
            if (typeof this.order.asfin_account_id === 'undefined') {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.order.asfin_account_id = null;
            }

            // Missing an linked ASFiN account and order was successful without order insurance.
            if (
                (typeof this.order.asfin_account_id !== 'number' ||
                    isNaN(this.order.asfin_account_id) ||
                    this.order.asfin_account_id <= 0) &&
                !this.order.order_insurance &&
                this.order.was_successful
            ) {
                return false;
            }

            // Read the validity start of order and the current date.
            // Because if the validity periods already started an change is no longer possible.
            return new Date(this.order.validity_start).getTime() > new Date().getTime();
        },

        shouldChangeValidity() {
            return this.order && this.order.product.duration && this.order.product.duration !== 365;
        },

        /**
         * @returns {VueI18n.TranslateResult}
         */
        loadingAnimationText() {
            // If the customer has enabled the option to order the vignette with an insurance, then we have to
            // check if the order was already completed. In the case the order was completed, we have to show
            // our default loading text. Otherwise, we will show a simpler message like "The update is ongoing please wait".
            if (
                typeof this.order.order_insurance === 'boolean' &&
                this.order.order_insurance &&
                !this.order.was_successful
            ) {
                return this.$t('orderChange.processingUpdate');
            }

            return this.$t('orderChange.processing');
        },

        /**
         * Determine if the order is transferable to a new ASFiN AG account.
         *
         * @returns {boolean}
         * */
        isTransferable() {
            // In the case that the transfer is not allowed by the system configuration.
            // Or simply the end date of validity isn't known, which is required to handle a initial
            // check of is a change possible or not.
            if (
                !this.isTransferOfProductEnabled ||
                typeof this.order.validity_end !== 'string' ||
                !this.order.validity_end.trim().length ||
                (!this.order.was_successful && this.order.order_insurance)
            ) {
                return false;
            }

            return new Date(this.order.validity_end).getTime() > new Date().getTime();
        },

        /**
         * Determine if the transfer product form should be visible.
         *
         * @returns {Boolean}
         * */
        showTransferForm() {
            return this.changeProductOwner && this.isTransferable;
        },

        /**
         * Determine the currently selected plate country.
         *
         * @returns {*}
         * */
        selectedLicensePlateCountry() {
            const result = this.licensePlateCountries.filter(
                (country) => country.country_code === this.licensePlateChange.country
            );

            if (!result.length) {
                return null;
            }

            return result[0];
        },

        /**
         * @returns {string}
         * */
        validityDatepickerValue() {
            const date = new Date(this.order.validity_start);

            return `${this.formatDateNumber(date.getDate())}.${this.formatDateNumber(
                date.getMonth() + 1
            )}.${date.getFullYear()}`;
        },

        /**
         * Prepare the form data for submit.
         *
         * @returns {FormData}
         * */
        requestFormData() {
            // Create an form instance to handle the validation of request.
            const form = new FormData();

            if (this.changeLicensePlate) {
                form.append('country_code', this.licensePlateChange.country);
                form.append('license', this.licensePlateChange.plate);
                form.append('license_confirmation', this.licensePlateChange.plateConfirm);
            }

            if (this.changeValidity) {
                form.append('validity_start', this.validityChange.date);
            }

            if (this.changeProductOwner) {
                form.append('email', this.transferProduct.email);
                form.append('email_confirmation', this.transferProduct.emailConfirm);
            }

            return form;
        },

        /**
         * Determine if the order was already transferred to a new account.
         *
         * @returns {Boolean}
         * */
        productWasTransferred() {
            return typeof this.order.transferred === 'boolean' && this.order.transferred;
        },
    },

    methods: {
        /**
         * Handle the update of modal data and fade in the modal, to handle the action.
         *
         * @param {MouseEvent} $event
         * @param {string} type
         *
         * @returns {void}
         */
        onChangeButtonClick($event, type) {
            $event.preventDefault();

            let title = '',
                confirmLabel = '',
                licensePlate = '',
                validityStart = '',
                transferEmail = '';
            let isOngoing = false,
                id = null,
                intervalCallback = null;

            // In the case we don't have the permission to ignore the provided ongoing update.
            if (this.ongoingUpdate !== null && this.ongoingUpdate.id !== null) {
                // Flag to indicate if the loading status should be displayed.
                let displayStatus = false;

                // Determine the correct type to display and adjust the prefill of the form.
                if (this.ongoingUpdate.isLicensePlateUpdate) {
                    this.changeLicensePlate.country = this.ongoingUpdate.licensePlateCountry;

                    type = 'license-plate';
                    licensePlate = this.ongoingUpdate.licensePlate;
                    displayStatus = true;
                } else if (this.ongoingUpdate.isValidityRangeUpdate) {
                    type = 'validity-start';
                    validityStart = this.ongoingUpdate.validityStart;
                    displayStatus = true;
                } else if (this.ongoingUpdate.isProductTransfer) {
                    type = 'transfer';
                    transferEmail = this.ongoingUpdate.productTransferEmail;
                    displayStatus = true;
                }

                if (displayStatus) {
                    isOngoing = true;
                    id = this.ongoingUpdate.id;
                    intervalCallback = () => {
                        this.checkJobStatus();
                    };
                }
            }

            switch (type) {
                case 'license-plate':
                    title = this.$t('orderChange.changeLicensePlateTitle');
                    confirmLabel = this.$t('orderChange.changeLicensePlateConfirm');
                    this.changeLicensePlate = true;
                    this.changeValidity = false;
                    this.changeProductOwner = false;

                    // Reset the previous inputs
                    this.licensePlateChange.plate = licensePlate;
                    this.licensePlateChange.plateConfirm = licensePlate;

                    break;

                case 'validity-start':
                    title = this.$t('orderChange.changeValidityStartTitle');
                    confirmLabel = this.$t('orderChange.changeValidityStartConfirm');
                    this.changeLicensePlate = false;
                    this.changeValidity = true;
                    this.changeProductOwner = false;

                    // Reset the inputs.
                    this.validityChange.date = validityStart;

                    break;

                case 'transfer':
                    if (!this.isTransferOfProductEnabled) {
                        throw new Error('The transfer of product is currently disabled!');
                    }

                    title = this.$t('orderChange.transferProductTitle');
                    confirmLabel = this.$t('orderChange.transferProductConfirm');

                    this.changeLicensePlate = false;
                    this.changeValidity = false;
                    this.changeProductOwner = true;

                    // Reset the inputs
                    this.transferProduct.email = transferEmail;
                    this.transferProduct.emailConfirm = transferEmail;

                    break;

                default:
                    throw new Error('Unknown action type provided.');
            }

            // Reset the change status
            this.change.isOngoing = isOngoing;
            this.change.id = id;
            this.change.interval =
                intervalCallback === null ? -1 : setInterval(intervalCallback, this.checkStatusInterval);
            this.change.completed = false;
            this.change.failureReason = null;

            // Update the modal and show it.
            this.modal.title = title;
            this.modal.confirmButtonLabel = confirmLabel;
            this.modal.show = true;
            this.modalIsVisible = true;
        },

        /**
         * Handle the callback of an modal button click.
         *
         * @param {MouseEvent} $event
         * @param {boolean} status
         *
         * @returns {void}
         */
        onModalButtonClick($event, status) {
            $event.preventDefault();

            // In the case the cancel button were clicked.
            if (!status) {
                // The modal is currently not closeable.
                if (this.change.isOngoing) {
                    return;
                }

                this.modal.show = false;
                this.modal.close = true;

                return;
            }

            if (!this.change.completed && !this.change.isOngoing) {
                this.submit(null);

                return;
            }

            if (this.change.isOngoing) {
                return;
            }

            this.modal.show = false;
            this.modal.hide = true;
        },

        /**
         * Handle the change of an input, to optional send an request to validate the form data.
         *
         * @param key
         * @param validateRequest
         *
         * @returns {Promise<void>}
         */
        async inputChange(key, validateRequest) {
            // We have an input key to update, than we should apply this to our
            // list of known inputs to validate.
            if (typeof key !== 'undefined') {
                this.changedInputs[key] = true;
            }

            // In the case that we should validate the input, we should send
            // the async request to handle this validation.
            if (typeof validateRequest === 'boolean' && validateRequest) {
                //this.debouncedValidation();
            }
        },

        /**
         * Format the date number into the expected format.
         *
         * @param {number} number
         *
         * @returns {string}
         */
        formatDateNumber(number) {
            return (number > 9 ? '' : '0') + number;
        },

        /**
         * Handle the callback of modal is now fully visible.
         *
         * @return {void}
         */
        onModalVisible() {
            setTimeout(() => {
                // In the case we have an datepicker, we should set the current
                // validity date.
                if (
                    typeof this.$refs.datepicker !== 'undefined' &&
                    typeof this.$refs.datepicker.flatpickr === 'object' &&
                    this.$refs.datepicker.flatpickr !== null
                ) {
                    this.$refs.datepicker.flatpickr.setDate(this.validityDatepickerValue, true);
                }
            }, 15);
        },

        /**
         * Handle the validation of form fields.
         *
         * @returns {Promise<*>}
         */
        async validateChangeRequest() {
            return axios
                .post('/' + this.$language + '/backend/validate/changeOrder/' + this.order.id, this.requestFormData)
                .then((result) => {
                    // In the case that the change request isn't valid we should provide
                    // the user the reason for request failures.
                    if (!result.data.valid) {
                        // Before we apply the errors, we should filter the empty fields,
                        // from response. To prevent an error message on not entered inputs.
                        for (const key in result.data.errors) {
                            if (typeof this.changedInputs[key] === 'undefined' || !this.changedInputs[key]) {
                                delete result.data.errors[key];
                            }
                        }

                        this.formErrors = result.data.errors;
                        this.isValid = false;

                        return false;
                    }

                    this.formErrors = {};
                    this.isValid = true;

                    return true;
                })
                .catch(() => {
                    this.isValid = false;
                });
        },

        /**
         * Handle the submit of form data to change the order configuration.
         *
         * @param $event
         *
         * @return {void}
         */
        submit($event) {
            // In the case we got an preventable event
            if (typeof $event === 'object' && $event !== null && typeof $event.preventDefault === 'function') {
                $event.preventDefault();
            }

            // In the case that there is already an request ongoing to change
            // the configuration of order. We should prevent another submit of
            // the data until the request were finished or failed.
            if (this.change.isOngoing) {
                return;
            }

            // Prevent further submits of requests until, the request were completed.
            this.change.isOngoing = true;
            this.change.failureReason = null;

            this.validateChangeRequest().then(() => {
                // In the case that the validation of the data fails, we should prevent
                // an further submit.
                if (!this.isValid) {
                    this.change.isOngoing = false;

                    return;
                }

                // Define the form url for the action.
                let formUrl = '/update-order/',
                    simpleUpdate = false;

                // Update only the credentials, if it's sent from order insurance view.
                if (!this.order.was_successful && this.order.order_insurance) {
                    formUrl += 'credentials/';
                    simpleUpdate = true;
                }

                // Now we can send the request to handle the change of product. In the case
                // that this request were successfully performed, we should create an interval
                // to check the status of job.
                axios
                    .post('/' + this.$language + formUrl + this.order.id, this.requestFormData)
                    .then((response) => {
                        // In the case that the request failed, we should cancel the process.
                        if (!response.data.success || typeof response.data.id !== 'number' || isNaN(response.data.id)) {
                            this.change.isOngoing = false;
                            this.change.failureReason = simpleUpdate ? response.data.message : null;

                            return;
                        }

                        // If the request was sent by the simple update, we have to read the message from the response.
                        // Because no job will be dispatched the update is directly completed. Which means we don't need
                        // to check a status of a job in the interval structure.
                        if (simpleUpdate) {
                            this.change.completed = true;
                            this.change.isOngoing = false;
                            this.ongoingUpdate = null;
                            this.modal.confirmButtonLabel = this.$t('frontend.close');

                            // Additionally, we have to trigger an update of order data.
                            // To assure the displayed data is up-to-date.
                            this.$emit('order-updated', response.data.order);

                            setTimeout(() => {
                                window.location.href = location.href;
                            }, 1700);

                            return;
                        }

                        // In the first step we have to reset the checking interval,
                        // to assure they are not multiple intervals running at the same time.
                        this.resetCheckingInterval();

                        // Assure to show the correct modal if it was closed.
                        this.ongoingUpdate = {
                            id: response.data.id,
                            isLicensePlateUpdate: this.changeLicensePlate,
                            licensePlateCountry: this.licensePlateChange.country,
                            licensePlate: this.licensePlateChange.plate,
                            isValidityRangeUpdate: this.changeValidity,
                            validityStart: this.validityChange.date,
                            isProductTransfer: this.changeProductOwner,
                            productTransferEmail: this.transferProduct.email,
                        };

                        // Now update the id of change request and create an new interval.
                        this.change.id = response.data.id;
                        this.change.completed = false;
                        this.change.interval = setInterval(() => {
                            this.checkJobStatus();
                        }, this.checkStatusInterval);
                    })
                    .catch(() => {
                        this.change.isOngoing = false;
                    });
            });
        },

        /**
         * Handle the reset of checking interval.
         *
         * @returns {this}
         */
        resetCheckingInterval() {
            // In the case we have an interval, we have to clear it
            if (this.change.interval !== -1) {
                clearInterval(this.change.interval);
                this.change.interval = -1;
            }

            return this;
        },

        /**
         * Handle the check of job status by sending an request to an controller.
         *
         * @returns {*}
         */
        checkJobStatus() {
            return axios
                .get('/' + this.$language + '/update-order/status/' + this.change.id + '?order_id=' + this.order.id)
                .then((response) => {
                    // In the case that the request were not completed yet, we have
                    // to recheck the status by using the interval. But in the case we
                    // receive an status, which indicates any kind of issue we should
                    // update the interval too.
                    if (!response.data.success) {
                        switch (response.data.reason) {
                            case 'failure':
                                this.change.failureReason = response.data.message;
                                this.change.isOngoing = false;
                                this.ongoingUpdate = null;
                                this.resetCheckingInterval();

                                break;

                            case 'order':
                            case 'not_found':
                                this.change.isOngoing = false;
                                this.ongoingUpdate = null;
                                this.resetCheckingInterval();

                                break;
                        }

                        return;
                    }

                    // In the first step we have to reset the interval, because the job is
                    // finished and we can show an success message.
                    this.resetCheckingInterval();
                    this.change.completed = true;
                    this.change.isOngoing = false;
                    this.ongoingUpdate = null;
                    this.modal.confirmButtonLabel = this.$t('frontend.close');

                    // Additional we have to trigger an update of order data.
                    // To assure the displayed data is up-to-date.
                    this.$emit('order-updated', response.data.order);

                    setTimeout(() => {
                        window.location.href = location.href;
                    }, 1700);
                })
                .catch(() => {
                    this.change.isOngoing = false;
                    this.ongoingUpdate = null;
                    this.resetCheckingInterval();
                });
        },
    },

    mounted() {
        if (this.defaultLicensePlateCountry) {
            this.licensePlateChange.country = this.defaultLicensePlateCountry;
            this.changedInputs.country_code = true;
        }
    },
};
</script>
<style lang="scss">
.license-plate-input-container {
    .license-plate-input {
        .license-plate-input-field {
            .form-control {
                font-size: clamp(1rem, 5vw, 2rem) !important;
            }
        }
    }
}

.change-order-buttons-container {
    #change-order-modal .modal-header {
        padding-bottom: 0.125rem;
    }

    #change-order-modal .modal-body {
        padding-top: 0.125rem;
    }

    .loader-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 9999999;

        background: rgba(255, 255, 255, 0.8);
    }
    .loader-container .preloader .status {
        .spinner {
            position: relative;
            text-align: center;

            .spinner-inner {
                position: relative;
                margin: auto auto 1rem;

                .double-bounce1,
                .double-bounce2 {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;

                    background-color: #2f55d4;
                    opacity: 0.6;

                    -webkit-animation: sk-bounce 2s infinite ease-in-out;
                    animation: sk-bounce 2s infinite ease-in-out;
                }

                .double-bounce2 {
                    -webkit-animation-delay: -1s;
                    animation-delay: -1s;
                }

                width: 2.5rem;
                height: 2.5rem;
            }

            .spinner-text {
                font-weight: bolder;
            }
        }

        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        -webkit-transform: translateY(-50%);
        transform: translateY(-50%);
    }
}

@media (max-width: 991px) {
    .row {
        margin-right: -15px;
        margin-left: -15px;
    }
}
</style>
